'use client';

import { APP_STORE_URL, detectMobilePlatform } from 'app/invite/invite.const';
import { useCallback, useEffect, useRef, useState } from 'react';
import { buildAndroidAppRootIntentUrl, buildAppRootDeepLink } from './RenewalNoticeModal.const';

/** '오늘 하루 안보기' 를 누른 사용자의 숨김 만료 시각(다음 로컬 자정, epoch ms 문자열). localStorage 에 둔다. */
export const RENEWAL_NOTICE_HIDDEN_UNTIL_KEY = 'renewalNotice-hiddenUntil';

/** 리뉴얼 안내 노출 시작 시각 (2026-10-07 10:30 KST). 이 시각 전에 진입하면 열지 않는다. */
export const RENEWAL_NOTICE_START_AT = Date.parse('2026-10-07T10:30:00+09:00');

const IOS_APP_LAUNCH_TIMEOUT_MS = 1500;

export type RenewalNoticeVariant = 'mobile' | 'pc';

export const useRenewalNotice = (): {
  isOpen: boolean;
  isChecked: boolean;
  variant: RenewalNoticeVariant;
  close: () => void;
  hideToday: () => void;
  start: () => void;
} => {
  // SSR 과 첫 클라이언트 렌더가 어긋나지 않도록 false 로 시작하고, 마운트 후에 연다.
  const [isOpen, setIsOpen] = useState(false);
  // 숨김 만료 시각을 읽기 전에는 isOpen=false 가 "닫힘"이 아니라 "아직 모름"이다. 호출부가 이 둘을
  // 구분해야 리뉴얼 안내 뒤에 뜰 팝업이 첫 렌더에 잠깐 마운트됐다 사라지지 않는다.
  const [isChecked, setIsChecked] = useState(false);
  // 데스크톱 UA('other')면 시작하기 버튼 대신 스토어 QR 을 보여준다. SSR 과 첫 렌더가 어긋나지 않도록
  // 'mobile' 로 시작하고 마운트 후에 정한다.
  const [variant, setVariant] = useState<RenewalNoticeVariant>('mobile');
  // 마지막으로 예약한 iOS 스토어 폴백의 취소 핸들. 다시 누르거나 언마운트될 때 먼저 정리한다.
  const pendingStoreRedirectCancelRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    setIsChecked(true);

    if (
      detectMobilePlatform(window.navigator.userAgent, window.navigator.maxTouchPoints) === 'other'
    )
      setVariant('pc');

    // 판정은 마운트 시점 한 번뿐이다 — 시작 시각 전에 들어와 머물러 있으면 다음 진입부터 뜬다.
    if (Date.now() < RENEWAL_NOTICE_START_AT) return;

    try {
      const hiddenUntil = Number(window.localStorage.getItem(RENEWAL_NOTICE_HIDDEN_UNTIL_KEY));

      // 값이 없거나(null → 0) 숫자가 아니면 숨김 기록이 없는 것으로 본다. 만료 시각 정각부터는 다시 연다.
      if (Number.isFinite(hiddenUntil) && Date.now() < hiddenUntil) return;
    } catch {
      // 사파리 프라이빗 모드 등에서 storage 접근이 throw 할 수 있다 — 읽지 못하면 열린 채로 둔다.
    }

    setIsOpen(true);
  }, []);

  useEffect(() => {
    return () => {
      pendingStoreRedirectCancelRef.current?.();
    };
  }, []);

  // X 는 이 화면에서만 닫는다 — 아무것도 저장하지 않으므로 새로고침하면 다시 뜬다.
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // '오늘 하루 안보기' — 닫고 다음 로컬 자정까지 숨긴다. 탭을 닫아도 유지돼야 해서 localStorage 에 둔다.
  const hideToday = useCallback(() => {
    setIsOpen(false);

    try {
      const now = new Date(Date.now());
      const nextMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);

      window.localStorage.setItem(RENEWAL_NOTICE_HIDDEN_UNTIL_KEY, String(nextMidnight.getTime()));
    } catch {
      // 저장에 실패해도 팝업은 닫는다 — 새로고침 시 다시 뜰 뿐이다.
    }
  }, []);

  // 앱이 실제로 열리면 브라우저는 백그라운드로 전환된다. 이 타이밍에 타이머를 취소하지 않으면
  // 사용자가 앱에서 브라우저로 돌아왔을 때 스토어로 잘못 이동한다 (InviteClientPage 와 같은 패턴).
  const scheduleStoreRedirectFallback = useCallback((storeUrl: string) => {
    pendingStoreRedirectCancelRef.current?.();

    let hasRedirectedToStore = false;

    const storeRedirectTimer = setTimeout(() => {
      hasRedirectedToStore = true;
      window.location.href = storeUrl;
    }, IOS_APP_LAUNCH_TIMEOUT_MS);

    const cancelStoreRedirect = () => {
      if (hasRedirectedToStore) return;
      clearTimeout(storeRedirectTimer);
      document.removeEventListener('visibilitychange', cancelStoreRedirect);
      window.removeEventListener('pagehide', cancelStoreRedirect);
      pendingStoreRedirectCancelRef.current = null;
    };

    document.addEventListener('visibilitychange', cancelStoreRedirect);
    window.addEventListener('pagehide', cancelStoreRedirect);

    pendingStoreRedirectCancelRef.current = cancelStoreRedirect;
  }, []);

  // 앱으로 이동할 뿐 팝업은 닫지 않는다 — 앱이 없어 돌아온 사용자가 안내를 다시 볼 수 있어야 한다.
  const start = useCallback(() => {
    const platform = detectMobilePlatform(
      window.navigator.userAgent,
      window.navigator.maxTouchPoints,
    );

    if (platform === 'android') {
      // intent URL 의 S.browser_fallback_url 이 미설치 시 Play 스토어 폴백을 처리한다.
      window.location.href = buildAndroidAppRootIntentUrl();
      return;
    }

    window.location.href = buildAppRootDeepLink();

    // 'other' 는 어느 스토어로 보내야 할지 판단이 애매하므로 iOS 에만 스토어 폴백을 건다.
    if (platform === 'ios') {
      scheduleStoreRedirectFallback(APP_STORE_URL);
    }
  }, [scheduleStoreRedirectFallback]);

  return { close, hideToday, isChecked, isOpen, start, variant };
};
